import {
  FormLabel,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import './ICVActivityStyles.css';
import { Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils } from "../../../utils/FormUtils";
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { icvGetCropBySearchAPICall, updateQCFeedback } from "../../../api/icvActivityAPI";
import { LoggedUserType } from "../../../models/GlobalModels";
import { loginSelector } from "../../../redux/slices/loginSlice";
import { CROP_SOWN_OPTIONS, ICV_STATUS, IMAGE_QUALITY_OPTIONS } from "../../../utils/Constant";
import { CustomRadioGroup, CustomTextField } from "../../../components/CustomFormComponents";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown";

type FormValues = {
  imageQuality: string;
  imageQualityRemarks: string;
  cropSown: string;
  cropSownRemarks: string;
  additionalRemarks: string;
  cropName: string[];
};

const ICVActivityQCForm = forwardRef(({
  formData,
  selectedStatus,
  setSelectedStatus,
  hideButtons = false,
  setIsFormValid,
  setIsSubmitting
}: any, ref) => {
  const user: LoggedUserType = useSelector(loginSelector).user;
  const [formDisabled, setFormDisabled] = useState<boolean>(false);
  const [selectedCropNames, setSelectedCropNames] = useState<string[]>([]);
  const dispatch: any = useDispatch();
  const {
    storeICVGetCropsMastersData,
    storeICVStatusUpdateRes
  } = useSelector((state: any) => state.icvActivity);

  const cropMastersData = FormUtils.convertDataList(
    storeICVGetCropsMastersData?.data ?? []
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    defaultValues: {
      imageQuality: '',
      imageQualityRemarks: '',
      cropSown: '',
      cropSownRemarks: '',
      additionalRemarks: '',
      cropName: [],
    },
    mode: 'all',
  });

  const imageQuality = watch("imageQuality");
  const cropSown = watch("cropSown");
  const imageQualityRemarks = watch("imageQualityRemarks");
  const cropSownRemarks = watch("cropSownRemarks");
  const cropName = watch("cropName");

  const onSubmit = (data: FormValues) => {
    if(isValid) {    
      setIsSubmitting(true);
      const cropNameArray = selectedCropNames;
      
      dispatch(updateQCFeedback(
        formData?.icvId,
        selectedStatus === 'Reassigned' ? ICV_STATUS.REASSIGNED : selectedStatus === 'Completed' ? ICV_STATUS.COMPLETED : null,
        formData?.qcMailId,
        user?.employeeName,
        user?.emailId,
        data?.imageQuality === 'acceptable' ? 'Acceptable' : data?.imageQuality === 'nonAcceptable' ? 'Non Acceptable' : null,
        data?.imageQualityRemarks,
        data?.cropSown === 'yes' ? 'Yes' : data?.cropSown === 'no' ? 'No' : null,
        data?.cropSownRemarks,
        cropNameArray,
        data?.additionalRemarks
      ));
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    }
  }));

  useEffect(() => {
    if (setIsFormValid) {
      setIsFormValid(isValid);
    }
  }, [isValid, setIsFormValid]);

  useEffect(() => {
    if(formData?.icvStatusId !== ICV_STATUS.QC_IN_PROGRESS) {
      setValue('imageQuality', formData?.imageQualityCheckByQc === 'Acceptable' ? 'acceptable' : formData?.imageQualityCheckByQc === 'Non Acceptable' ? 'nonAcceptable' : '');
      setValue('cropSown', formData?.cropSownCheckByQc === 'Yes' ? 'yes' : formData?.cropSownCheckByQc === 'No' ? 'no' : '');
      setValue('imageQualityRemarks', formData?.imageQualityRemarksByQc);
      setValue('cropSownRemarks', formData?.cropSownRemarksByQc);
      setValue('cropName', []);
      setValue('additionalRemarks', formData?.additionalRemarksByQc);
      
      if (formData?.cropNameByQc) {
        let cropNames: string[] = [];
        if (Array.isArray(formData.cropNameByQc)) {
          cropNames = formData.cropNameByQc;
        } else if (typeof formData.cropNameByQc === 'string') {
          cropNames = formData.cropNameByQc.split(',').map((name: string) => name.trim()).filter(Boolean);
        }
        setSelectedCropNames(cropNames);
        setValue('cropName', cropNames);
      } else {
        setSelectedCropNames([]);
        setValue('cropName', []);
      }
    }

    if (
        formData?.icvStatusId === ICV_STATUS.QC_IN_PROGRESS &&
        user?.permissions?.includes('Quality-Check-feedback')
    ) {
      setFormDisabled(false);
    }
    else{
      setFormDisabled(true);
    }
    dispatch(icvGetCropBySearchAPICall("",formData?.stateId,formData?.districtId))
  }, [])

  useEffect(() => {
    if (storeICVStatusUpdateRes && !(storeICVStatusUpdateRes?.statusCode === 1 && storeICVStatusUpdateRes?.data === 1)) {
      setIsSubmitting(false);
    }
  }, [storeICVStatusUpdateRes, setIsSubmitting]);

  const handleCropNameChange = (selectedCrops: any[]) => {
    setSelectedCropNames(selectedCrops);
    setValue('cropName', selectedCrops, {shouldValidate: true});
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="qc-form-section">
        {/* Image Quality */}
        <div className="image-quality w-100">
          <CustomRadioGroup
            name="imageQuality"
            control={control}
            label="Image Quality"
            options={IMAGE_QUALITY_OPTIONS}
            required={true}
            disabled={formDisabled}
            rules={{ required: 'Image quality is required' }}
            onChange={(value) => {
              if (value === 'acceptable') {
                setValue('imageQualityRemarks', '', { shouldValidate: true });
              }
            }}
            error={errors.imageQuality}
          />

          {imageQuality === "nonAcceptable" && (
            <CustomTextField
              name="imageQualityRemarks"
              control={control}
              label="Remarks"
              required={true}
              disabled={formDisabled}
              rules={{ required: 'Image quality remarks are required' }}
            />
          )}
        </div>

        {/* Crop Sown */}
        <div className="crop-sown">
          <CustomRadioGroup
            name="cropSown"
            control={control}
            label="Crop Sown"
            options={CROP_SOWN_OPTIONS}
            required={true}
            disabled={formDisabled}
            rules={{ required: 'Crop sown is required' }}
            onChange={(value) => {
              if (value === 'yes') {
                setValue('cropSownRemarks', '');
              } else if (value === 'no') {
                setSelectedCropNames([]);
              }
            }}
            error={errors.cropSown}
          />

          {cropSown === "no" && (
            <div>
              <CustomTextField
                name="cropSownRemarks"
                control={control}
                label="Remarks"
                required={true}
                disabled={formDisabled}
                rules={{ required: 'Crop sown remarks are required' }}
              />
            </div>
          )}

          {cropSown === "yes" && (
            <div className="crop-name-row">
              <span className="crop-name-label">Crop Name:
                <span className="required">*</span>
              </span>
              {formDisabled ? (
                <div className="crop-name-display">
                  <Tooltip 
                    title={selectedCropNames.length > 0 ? selectedCropNames.join(', ') : 'N/A'}
                    placement="bottom-start"
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'black',
                          color: 'white',
                          fontSize: '12px',
                          '& .MuiTooltip-arrow': {
                            color: 'black',
                          },
                        },
                      },
                    }}
                  >
                    <span className="crop-name-value">
                      {selectedCropNames.length > 0 
                        ? selectedCropNames.join(', ') 
                        : 'N/A'
                      }
                    </span>
                  </Tooltip>
                </div>
              ) : (
                  <div className="crop-name-input-container">
                    <Controller
                      name="cropName"
                      control={control}
                      rules={{required: 'Crop name is required'}}
                      render={({ field, fieldState: { error } }) => (
                        <div style={{width: '100%'}}>
                          <MultiSelectDropdown
                            options={cropMastersData}
                            label=""
                            value={selectedCropNames}
                            onChange={handleCropNameChange}
                            storeLabels={true}
                            showSelectAll={false}
                            showClearAll={true}
                            optional={false}
                          />
                          {error && (
                            <span style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                              {error.message}
                            </span>
                          )}
                        </div>
                      )}
                    />
                  </div>
                )}
            </div>
          )}
        </div>

        {/* Additional Remarks */}
        <div className="additional-remarks mt-2">
          <p></p>
          <FormLabel component="legend" sx={{fontSize: '14px'}}>Additional Remarks</FormLabel>
          <CustomTextField
            name="additionalRemarks"
            control={control}
            label=""
            disabled={formDisabled}
            width="100%"
          />
        </div>
      </div>
    </form>
  );
});

export default ICVActivityQCForm;